import { Area } from 'react-easy-crop';

const MAX_OUTPUT_SIZE = 512;

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Renders the cropped, rotated selection onto an offscreen canvas and
 * returns it as a JPEG Blob, capped at MAX_OUTPUT_SIZE px per side --
 * avatars are only ever displayed small, so there's no reason to upload
 * whatever multi-megapixel original the camera produced. Standard
 * two-pass approach (react-easy-crop's own documented recipe): first draw
 * the full rotated image onto a canvas sized to its rotated bounding box,
 * then cut the crop rectangle out of that onto the final canvas.
 */
export const getCroppedImageBlob = async (
  imageSrc: string,
  cropAreaPixels: Area,
  rotationDegrees: number
): Promise<Blob> => {
  const image = await loadImage(imageSrc);
  const rotation = toRadians(rotationDegrees);

  const sin = Math.abs(Math.sin(rotation));
  const cos = Math.abs(Math.cos(rotation));
  const rotatedWidth = image.width * cos + image.height * sin;
  const rotatedHeight = image.width * sin + image.height * cos;

  const rotateCanvas = document.createElement('canvas');
  rotateCanvas.width = rotatedWidth;
  rotateCanvas.height = rotatedHeight;
  const rotateCtx = rotateCanvas.getContext('2d');
  if (!rotateCtx) throw new Error('Canvas 2D context unavailable');

  rotateCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
  rotateCtx.rotate(rotation);
  rotateCtx.drawImage(image, -image.width / 2, -image.height / 2);

  const outputSize = Math.min(MAX_OUTPUT_SIZE, Math.max(cropAreaPixels.width, cropAreaPixels.height));
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = outputSize;
  outputCanvas.height = outputSize;
  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) throw new Error('Canvas 2D context unavailable');

  outputCtx.drawImage(
    rotateCanvas,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    outputCanvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))),
      'image/jpeg',
      0.92
    );
  });
};
